import type { SequenceStep } from '~/components/Sequence/sequenceSteps';

/**
 * Horizontal layout for the sequence canvas.
 *
 * Every step's node is centered on the spine (x = `cx`). A node can be narrow (an icon tile) or wide (a
 * wait pill with the days/weeks toggle, an await pill, a group button-box), so a fixed offset for the
 * side branches (a spawn's "runs alongside" box, an await's not-accepted arm) inevitably overlaps the
 * widest node. Instead each side branch gets its OWN vertical LANE to the right of the WIDEST node, and
 * lanes are packed left-to-right in step order — so nothing overlaps however the steps change.
 */

/** Gap between the widest node's right edge and the first branch lane. */
const LANE_GAP = 48;
/**
 * A not-accepted arm draws its nodes CENTERED on the lane's x (unlike a spawn's left-aligned box), so the
 * lane must leave room for the widest node that can sit there (a wait pill) to not poke back into the spine.
 */
const LANE_NODE_HALF = 110;
/** Width reserved per branch lane (a spawn box / not-accepted column + breathing room). */
export const LANE_W = 320;

/** A step's text preview sits this far right of its node's right edge… */
export const PREVIEW_GAP = 12;
/** …and this wide by default (w-56) — capped narrower when branch lanes claim the gutter. The history column's
 *  steps above the sequence use the same two, so their text lines up with the graph's own previews. */
export const PREVIEW_W = 224;

/** Estimated half-width (px) of a node centered on the spine — drives where branches can safely sit. */
export function spineNodeHalfWidth(step: SequenceStep): number {
  if (step.type === 'wait') return 110; // clock + number + days/weeks segment
  if (step.type === 'await_connect') return 125; // clock + the "Await accept · up to N days" pill (longest label)
  if (step.type === 'switch') return 82; // dataflow icon + Age/~AUM segment (a wait-pill-like node)
  if (step.type === 'group') {
    const chips = step.arms?.filter((a) => a.length > 0).length ?? 0;
    return (16 + (chips + 1) * 44) / 2; // the inline button-box: padding + chips + "+"
  }
  return 20; // a plain icon tile (~36px)
}

/** Whether a step draws a side branch that needs its own right-hand lane: a spawn (always) or an await
 * WITH an arm. A switch does NOT — it's terminal and fans its route branches DOWN below the node (see
 * SequenceSwitchBranches), so it needs no lane to the right. */
export function stepHasBranchLane(step: SequenceStep): boolean {
  if (step.type === 'spawn') return true;
  return step.type === 'await_connect' && (step.branches?.notAccepted?.length ?? 0) > 0;
}

export interface BranchLanes {
  /** Half-width of the widest node on the spine (so branches clear even the widest one). */
  maxSpineHalfW: number;
  /** X of the first branch lane (right of the widest node). */
  laneX0: number;
  /** Number of lanes in use. */
  laneCount: number;
  /** The lane index (0-based) assigned to step `i`, or null if it has no branch. */
  laneOf: (i: number) => number | null;
  /** X (left edge) of step `i`'s branch lane. Falls back to `laneX0` for a step with no lane. */
  branchXFor: (i: number) => number;
  /** X where the "if not accepted" STUB button (an await with no arm yet) sits — just past its own node. */
  stubXFor: (i: number) => number;
  /** Center X of the connector run between node `i`'s right edge and its lane (for the branch's label). */
  labelXFor: (i: number) => number;
  /** Right edge of the whole canvas content (for width reservation). */
  contentRight: (cx: number) => number;
}

/**
 * Compute the branch-lane layout for a spine of `steps` centered at `cx`. Pure + recomputed per render.
 *
 * `fanRightEdge` (absolute canvas x; 0 = none) is the right edge of the widest switch fan on the spine. A
 * switch fans its route branches DOWN below the node and spreads RIGHT, while a spawn's / await arm's lane
 * runs vertically DOWN past the switch's row — so the lanes must clear the fan's right edge too, not just
 * the widest spine node, or a lane's nodes overlap the fan's right columns (e.g. its "+ range" tile). The
 * editor computes it from the fan geometry (switchFanTotalWidth) and passes it in; this module stays
 * dependency-free.
 */
export function computeBranchLanes(steps: SequenceStep[], cx: number, fanRightEdge = 0): BranchLanes {
  const maxSpineHalfW = steps.reduce((m, s) => Math.max(m, spineNodeHalfWidth(s)), 20);
  // Leave room past the spine's widest node OR the widest switch fan (whichever reaches further right), plus
  // a lane's own widest (centered) node — so neither the spine, the fan, nor the lane pokes into another.
  const laneX0 = Math.max(cx + maxSpineHalfW, fanRightEdge) + LANE_GAP + LANE_NODE_HALF;

  // Assign the INNERMOST lane (0) to the LAST branch and work outward. A branch's connector runs from the
  // spine across every INNER lane at that branch's OWN y; putting later (lower) branches in the inner lanes
  // means an earlier branch's long connector only crosses lanes whose (later) branches start BELOW it — i.e.
  // empty at that y — so no connector ever crosses another branch's nodes.
  const lane = new Map<number, number>();
  const branchSteps = steps.map((s, i) => (stepHasBranchLane(s) ? i : -1)).filter((i) => i >= 0);
  [...branchSteps].reverse().forEach((i, laneIdx) => lane.set(i, laneIdx));

  const nodeRight = (i: number) => cx + spineNodeHalfWidth(steps[i] ?? { type: 'follow' });
  const laneOf = (i: number) => lane.get(i) ?? null;
  const branchXFor = (i: number) => laneX0 + (lane.get(i) ?? 0) * LANE_W;
  const stubXFor = (i: number) => nodeRight(i) + 24;
  // The label sits on the connector, between the node's right edge and the lane's LEFT (node) edge.
  const labelXFor = (i: number) => (nodeRight(i) + branchXFor(i) - LANE_NODE_HALF) / 2;

  return {
    maxSpineHalfW,
    laneX0,
    laneCount: lane.size,
    laneOf,
    branchXFor,
    stubXFor,
    labelXFor,
    contentRight: (c: number) => (lane.size === 0 ? c + maxSpineHalfW + 40 : laneX0 + lane.size * LANE_W),
  };
}
